import { useMemo } from 'react';
import { keyframes } from '@emotion/react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography, alpha, useTheme } from '@mui/material';
import { ArrowBack, HomeOutlined } from '@mui/icons-material';
import useTenant from '../../hooks/useTenant';

// Animações — todas desligadas pra quem pediu menos movimento no sistema (prefers-reduced-motion)
const balanco = keyframes`
  0%, 100% { transform: rotate(-9deg); }
  50% { transform: rotate(9deg); }
`;
const brilho = keyframes`
  0% { transform: translateX(-160%) rotate(25deg); }
  60%, 100% { transform: translateX(260%) rotate(25deg); }
`;
const queda = keyframes`
  0% { transform: translate3d(0, -12vh, 0) rotate(0deg); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translate3d(var(--deriva), 112vh, 0) rotate(var(--giro)); opacity: 0.9; }
`;
const surgir = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: none; }
`;
const flutuar = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

/** Medalha pendurada na fita, balançando — é o "0" do 404. */
function Medalha({ cor }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: { xs: 96, sm: 132 },
        height: { xs: 150, sm: 200 },
        mx: { xs: 0.5, sm: 1 },
        transformOrigin: '50% 0',
        animation: `${balanco} 3.2s ease-in-out infinite`,
      }}
    >
      <svg viewBox="0 0 132 200" width="100%" height="100%" aria-hidden="true">
        {/* Fita */}
        <path d="M40 0 L66 70 L92 0 Z" fill={cor} opacity="0.95" />
        <path d="M40 0 L58 0 L78 58 L66 70 Z" fill="#000" opacity="0.18" />
        {/* Medalha */}
        <circle cx="66" cy="132" r="60" fill="#E8B53A" />
        <circle cx="66" cy="132" r="60" fill="url(#g)" />
        <circle cx="66" cy="132" r="46" fill="none" stroke="#B7841C" strokeWidth="5" strokeDasharray="6 7" />
        <path
          d="M66 104 l9 18 20 3 -14.5 14 3.5 20 -18 -9.5 -18 9.5 3.5 -20 -14.5 -14 20 -3 z"
          fill="#B7841C"
          opacity="0.85"
        />
        <defs>
          <radialGradient id="g" cx="35%" cy="30%" r="75%">
            <stop offset="0%" stopColor="#FFF4C4" stopOpacity="0.9" />
            <stop offset="55%" stopColor="#F2C14E" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#9C6D10" stopOpacity="0.45" />
          </radialGradient>
        </defs>
      </svg>
      {/* Brilho passando pela medalha */}
      <Box
        sx={{
          'position': 'absolute',
          'left': '5%',
          'right': '5%',
          'bottom': 0,
          'height': '61%',
          'borderRadius': '50%',
          'overflow': 'hidden',
          'pointerEvents': 'none',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-30%',
            left: 0,
            width: '28%',
            height: '160%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.75), transparent)',
            animation: `${brilho} 3.6s ease-in-out infinite`,
          },
        }}
      />
    </Box>
  );
}

/** Confete caindo ao fundo (posições sorteadas uma vez por montagem). */
function Confete({ cores }) {
  const pedacos = useMemo(
    () =>
      Array.from({ length: 26 }, (_, i) => ({
        left: Math.random() * 100,
        atraso: -Math.random() * 9,
        duracao: 6 + Math.random() * 6,
        deriva: `${(Math.random() - 0.5) * 30}vw`,
        giro: `${(Math.random() > 0.5 ? 1 : -1) * (360 + Math.random() * 540)}deg`,
        largura: 6 + Math.random() * 6,
        cor: cores[i % cores.length],
        redondo: Math.random() > 0.65,
      })),
    [cores],
  );
  return (
    <Box aria-hidden="true" sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {pedacos.map((p, i) => (
        <Box
          key={i}
          sx={{
            'position': 'absolute',
            'top': 0,
            'left': `${p.left}%`,
            'width': p.largura,
            'height': p.redondo ? p.largura : p.largura * 1.8,
            'borderRadius': p.redondo ? '50%' : '2px',
            'bgcolor': p.cor,
            'opacity': 0,
            '--deriva': p.deriva,
            '--giro': p.giro,
            'animation': `${queda} ${p.duracao}s linear ${p.atraso}s infinite`,
          }}
        />
      ))}
    </Box>
  );
}

/**
 * 404 com a cara do concurso: o "0" é uma medalha balançando na fita, confete ao fundo,
 * cores e logo do cliente (branding pelo subdomínio). Sem layout de login — página própria.
 */
function Page404() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { branding } = useTenant();
  const primary = branding?.corPrimaria || theme.palette.primary.main;
  const cores = useMemo(() => [primary, '#F2C14E', '#FFFFFF', alpha(primary, 0.6), '#E8B53A'], [primary]);
  const temHistorico = typeof window !== 'undefined' && window.history.length > 1;

  const digito = {
    fontSize: { xs: '6.5rem', sm: '9.5rem' },
    fontWeight: 900,
    lineHeight: 1,
    color: '#fff',
    textShadow: '0 10px 30px rgba(0,0,0,0.25)',
    animation: `${flutuar} 4s ease-in-out infinite`,
  };

  return (
    <Box
      sx={{
        'position': 'relative',
        'minHeight': '100vh',
        'overflow': 'hidden',
        'display': 'flex',
        'flexDirection': 'column',
        'alignItems': 'center',
        'justifyContent': 'center',
        'px': 3,
        'py': 6,
        'textAlign': 'center',
        'background': `radial-gradient(120% 90% at 50% 0%, ${alpha(primary, 0.85)} 0%, ${primary} 45%, ${theme.palette.augmentColor({ color: { main: primary } }).dark} 100%)`,
        '@media (prefers-reduced-motion: reduce)': { '& *': { animation: 'none !important' } },
      }}
    >
      <Helmet title={t('Page not found')} />
      <Confete cores={cores} />

      {branding?.logoInvertidoUrl && (
        <Box
          component="img"
          src={branding.logoInvertidoUrl}
          alt={branding?.nome ?? ''}
          sx={{
            position: 'absolute',
            top: { xs: 20, sm: 28 },
            height: { xs: 32, sm: 40 },
            objectFit: 'contain',
            zIndex: 1,
          }}
        />
      )}

      <Box sx={{ position: 'relative', zIndex: 1, animation: `${surgir} .6s ease-out both` }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="center" sx={{ mb: { xs: 2, sm: 3 } }}>
          <Box component="span" sx={{ ...digito, mt: { xs: 4, sm: 5 } }}>
            4
          </Box>
          <Medalha cor={alpha('#ffffff', 0.9)} />
          <Box component="span" sx={{ ...digito, mt: { xs: 4, sm: 5 }, animationDelay: '-2s' }}>
            4
          </Box>
        </Stack>

        <Typography
          component="h1"
          sx={{ color: '#fff', fontWeight: 800, fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 1 }}
        >
          {t("Oops! This page didn't make the podium.")}
        </Typography>
        <Typography
          sx={{
            color: alpha('#fff', 0.85),
            maxWidth: 440,
            mx: 'auto',
            mb: 4,
            fontSize: { xs: '0.95rem', sm: '1.05rem' },
          }}
        >
          {t('The address may have changed or the link is incomplete. How about going back and trying again?')}
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" alignItems="center">
          <Button
            component={Link}
            to="/"
            variant="contained"
            size="large"
            startIcon={<HomeOutlined />}
            sx={{
              'bgcolor': '#fff',
              'color': primary,
              'fontWeight': 700,
              'px': 3,
              '&:hover': { bgcolor: alpha('#fff', 0.9) },
            }}
          >
            {t('Go to the home page')}
          </Button>
          {temHistorico && (
            <Button
              onClick={() => navigate(-1)}
              variant="outlined"
              size="large"
              startIcon={<ArrowBack />}
              sx={{
                'color': '#fff',
                'borderColor': alpha('#fff', 0.6),
                'px': 3,
                '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.08) },
              }}
            >
              {t('Previous page')}
            </Button>
          )}
        </Stack>
      </Box>

      <Typography variant="caption" sx={{ position: 'absolute', bottom: 16, color: alpha('#fff', 0.6), zIndex: 1 }}>
        {t('Error 404')}
      </Typography>
    </Box>
  );
}

export default Page404;
